import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import {
  IntegracaoTerceiro,
  IntegracaoTerceiroPayload,
} from '../../../../../shared/services/api/c4-model/SistemasService';
import { Skeleton } from '@mui/material';
import { ModalIntegracaoTerceiros } from './ModalIntegracaoTerceiros';

type Row = {
  id: string;
  sistema: string;
  descricao: string;
};

type Props = {
  initialRows?: IntegracaoTerceiro[];
  onIntegracoesTerceiroChange?: (integracoes: IntegracaoTerceiro[]) => void;
  currentSystemId?: string;
  isLoading?: boolean;
};

const convertToRows = (integracoes: IntegracaoTerceiro[]): Row[] => {
  return (integracoes || []).map((item) => ({
    id: item.id,
    sistema: item.nomeSistema,
    descricao: item.descricao,
  }));
};

export const ListaIntegracaoTerceiros: React.FC<Props> = ({
  initialRows = [],
  onIntegracoesTerceiroChange,
  currentSystemId,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);

  useEffect(() => {
    if (initialRows && initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setEditingRow(row);
    setOpenModal(true);
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);

    if (onIntegracoesTerceiroChange) {
      const updatedIntegracoes = newRows.map((row) => ({
        id: row.id,
        nomeSistema: row.sistema,
        sistemaIntegracaoId: currentSystemId || '',
        descricao: row.descricao,
        sistemaIntegracao: row.sistema,
        acessoContainers: [],
      }));
      onIntegracoesTerceiroChange(updatedIntegracoes);
    }
  };

  const handleAddNew = () => {
    setEditingRow(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingRow(null);
  };

  const handleSaveModal = (
    values: IntegracaoTerceiroPayload,
    editId?: string
  ) => {
    let newRows: Row[] = [];
    if (editId && editingRow) {
      // Editando item existente
      newRows = rows.map((r) =>
        r.id === editId
          ? {
            id: editId,
            sistema: values.nomeSistema,
            descricao: values.descricao,
          }
          : r
      );
    } else {
      // Criando novo item
      const newRow: Row = {
        id: String(Date.now()),
        sistema: values.nomeSistema,
        descricao: values.descricao,
      };
      newRows = [...rows, newRow];
    }

    setRows(newRows);

    if (onIntegracoesTerceiroChange) {
      const updatedIntegracoes = newRows.map((row) => {
        // Se é o row que acabou de ser salvo, usar os acessoContainers do payload
        if (
          (editId && row.id === editId) ||
          (!editId && row.id === newRows[newRows.length - 1].id)
        ) {
          return {
            id: row.id,
            nomeSistema: row.sistema,
            sistemaIntegracaoId: currentSystemId || '',
            descricao: row.descricao,
            sistemaIntegracao: row.sistema,
            acessoContainers: (values.acessoContainers || []).map((ac) => ({
              id: `${row.id}-${Date.now()}`,
              integracaoTerceiroId: row.id,
              container: ac.container,
              acao: ac.acao,
              meioAcesso: ac.meioAcesso,
              integracaoTerceiro: row.sistema,
            })),
          };
        }
        // Para outros rows, manter os acessoContainers existentes
        const existingIntegracao = initialRows.find((ir) => ir.id === row.id);
        return {
          id: row.id,
          nomeSistema: row.sistema,
          sistemaIntegracaoId: currentSystemId || '',
          descricao: row.descricao,
          sistemaIntegracao: row.sistema,
          acessoContainers: existingIntegracao?.acessoContainers || [],
        };
      });
      onIntegracoesTerceiroChange(updatedIntegracoes);
    }

    handleCloseModal();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleAddNew}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Sistema</TableCell>
              <TableCell>Descrição</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3].map((n) => (
                  <TableRow key={n}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={150} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhuma integração com terceiros cadastrada
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(row.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.sistema}</TableCell>
                  <TableCell>{row.descricao}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ModalIntegracaoTerceiros
        open={openModal}
        onClose={handleCloseModal}
        onSave={handleSaveModal}
        currentSystemId={currentSystemId}
        initialValues={
          editingRow
            ? {
              id: editingRow.id,
              sistema: editingRow.sistema,
              descricao: editingRow.descricao,
              acessoContainers:
                  (
                    initialRows.find((r) => r.id === editingRow.id)
                      ?.acessoContainers || []
                  ).map((ac) => ({
                    container: ac.container,
                    acao: ac.acao,
                    meioAcesso: ac.meioAcesso,
                  })) || [],
            }
            : undefined
        }
      />
    </Box>
  );
};
